/**
 * # Ad-banner rotator (browser-side)
 *
 * The ad banner block ([data-banner-rotator]) renders the *whole* per-page banner
 * set into the DOM (see `item/item_adv_banner.php`); this module decides which
 * single banner is visible on every page-show.
 *
 * Why client-side: ASwap runs with `cacheOn: true`, so navigating back to a page
 * re-serves cached HTML without re-hitting the server — any server-side rotation
 * would be frozen for the SPA session (and page caching would freeze one banner
 * for all users). The browser is the only place that runs after a cached page is
 * restored, so rotation lives here. Called once on initial load and again from
 * the ASwap `onViewSwapComplete` hook for every swapped-in page.
 *
 * Per-set, per-user rotation:
 *   - Each page's set is identified by the sorted list of its banner ids (a
 *     banner shared across pages shares its rotation state; a different set gets
 *     its own). State = a shuffled order + a cursor, persisted in localStorage.
 *   - Each page-show advances the cursor → the next banner in the shuffled order,
 *     so reloads cycle through the whole set evenly in a random-looking order.
 *   - Persistence is gated on the live `functionality_storage` consent (read from
 *     the consent cookie each show — works on cached pages because the cookie
 *     *name* is baked into the markup, the value is read fresh). Without consent
 *     we just show a random banner and store nothing.
 *
 * Images of non-active slides are deferred (server emits `data-src`/`data-srcset`);
 * we restore them only on the slide we reveal, so only shown banners are fetched.
 */
import { getCookie } from 'rytm-helpers';

const ROOT_SELECTOR = '[data-banner-rotator]';
const SLIDE_SELECTOR = '[data-banner-slide]';
const STORAGE_KEY = 'rtmBannerRotation';
// Keep the persisted map bounded — a user only meets a handful of distinct sets.
const MAX_SETS = 30;

interface SetState {
  order: string[];
  cursor: number;
}

interface Store {
  sets: Record<string, SetState>;
  lru: string[];
}

/** Live functionality_storage consent, read from the consent cookie JSON. */
const hasConsent = (root: HTMLElement): boolean => {
  const cookieName = root.dataset.bannerConsentCookie;
  const group = root.dataset.bannerConsentGroup || 'functional';
  if (!cookieName) return false;
  try {
    const raw = getCookie(cookieName);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!parsed[group];
  } catch {
    return false;
  }
};

const loadStore = (): Store => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && parsed.sets) {
        return { sets: parsed.sets, lru: Array.isArray(parsed.lru) ? parsed.lru : [] };
      }
    }
  } catch {
    /* unreadable / disabled storage — start fresh */
  }
  return { sets: {}, lru: [] };
};

const saveStore = (store: Store): void => {
  while (store.lru.length > MAX_SETS) {
    const evict = store.lru.shift();
    if (evict) delete store.sets[evict];
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* storage full / disabled — rotation just won't persist */
  }
};

const touchLru = (store: Store, sig: string): void => {
  const i = store.lru.indexOf(sig);
  if (i !== -1) store.lru.splice(i, 1);
  store.lru.push(sig);
};

const shuffle = (ids: string[]): string[] => {
  const a = ids.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/** Same members (order-independent)? Used to detect a changed banner set. */
const sameMembers = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sorted = b.slice().sort();
  return a.slice().sort().every((id, i) => id === sorted[i]);
};

/**
 * Pick the next banner id for a set, advancing its persisted cursor. Reshuffles
 * on a completed cycle (avoiding an immediate repeat of the last banner).
 */
const nextConsentedId = (ids: string[], store: Store): string => {
  const sig = ids.slice().sort().join('.');
  let state = store.sets[sig];
  if (!state || !sameMembers(state.order, ids)) {
    state = { order: shuffle(ids), cursor: 0 };
  }
  if (state.cursor >= state.order.length) {
    const last = state.order[state.order.length - 1];
    const reshuffled = shuffle(ids);
    if (reshuffled.length > 1 && reshuffled[0] === last) {
      [reshuffled[0], reshuffled[1]] = [reshuffled[1], reshuffled[0]];
    }
    state = { order: reshuffled, cursor: 0 };
  }
  const id = state.order[state.cursor];
  state.cursor += 1;
  store.sets[sig] = state;
  touchLru(store, sig);
  return id;
};

/** Restore deferred images (data-src/data-srcset → src/srcset) on a slide. */
const restoreImages = (slide: HTMLElement): void => {
  slide.querySelectorAll<HTMLElement>('[data-src],[data-srcset]').forEach((el) => {
    const src = el.getAttribute('data-src');
    const srcset = el.getAttribute('data-srcset');
    if (src !== null) {
      el.setAttribute('src', src);
      el.removeAttribute('data-src');
    }
    if (srcset !== null) {
      el.setAttribute('srcset', srcset);
      el.removeAttribute('data-srcset');
    }
  });
};

const reveal = (slides: HTMLElement[], activeIdx: number): void => {
  slides.forEach((slide, i) => {
    const isActive = i === activeIdx;
    slide.hidden = !isActive;
    if (isActive) restoreImages(slide);
  });
};

const rotate = (root: HTMLElement): void => {
  const slides = Array.from(root.querySelectorAll<HTMLElement>(SLIDE_SELECTOR));
  if (!slides.length) return;

  let activeIdx = 0;
  if (slides.length > 1) {
    if (hasConsent(root)) {
      const store = loadStore();
      const ids = slides.map((s) => s.dataset.bannerId || '');
      const id = nextConsentedId(ids, store);
      activeIdx = Math.max(0, ids.indexOf(id));
      saveStore(store);
    } else {
      activeIdx = Math.floor(Math.random() * slides.length);
    }
  }
  reveal(slides, activeIdx);
};

/** Rotate every banner block currently in the DOM (no-op when there are none). */
export const initBannerRotator = (): void => {
  document.querySelectorAll<HTMLElement>(ROOT_SELECTOR).forEach((root) => rotate(root));
};
